import React, { useEffect, useRef, useState } from 'react';
import { FileText, X } from 'lucide-react';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import DatePicker from 'components/ui/DatePicker';
import { hoyCR } from 'lib/fechaCR';
import {
  RIESGOS, RIESGO_LABEL, RIESGO_PILL, TIPOS_EVALUACION, evaluacionVacia, prepararEvaluacion,
} from 'lib/arboles';
import { validarInformePdf } from 'services/arbolesService';
import { Campo, Texto } from './Campos';
import { mensajeError } from 'lib/errores';

// Evaluación de un árbol (arborista o inspección propia). Se guarda una vez:
// después no se edita, solo un administrador la anula con motivo.
// onGuardar(datos, informePdf) devuelve una promesa.
export default function FormEvaluacion({ open, onClose, arbol, onGuardar }) {
  const hoy = hoyCR();
  const [form, setForm] = useState(() => evaluacionVacia(hoy));
  const [informe, setInforme] = useState(null);
  const [errores, setErrores] = useState({});
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(evaluacionVacia(hoy));
    setInforme(null);
    setErrores({});
    setError('');
    setEnviando(false);
  }, [open, hoy]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const elegirPdf = (e) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    const problema = validarInformePdf(f);
    setErrores((x) => ({ ...x, informe: problema }));
    setInforme(problema ? null : f);
  };

  const guardar = async () => {
    if (enviando) return;
    const { datos, errores: e } = prepararEvaluacion(form, hoy);
    setErrores(e);
    if (Object.keys(e).length) {
      setError('Revise los campos marcados.');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      await onGuardar(datos, informe);
      onClose();
    } catch (err) {
      setError(mensajeError(err, 'No se pudo guardar la evaluación.'));
      setEnviando(false);
    }
  };

  return (
    <Modal open={open} onClose={enviando ? () => {} : onClose} title={`Nueva evaluación${arbol?.codigo ? ` · ${arbol.codigo}` : ''}`}
      maxWidth="max-w-2xl" cerrarConFondo={false}
      footer={(
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="lg" className="flex-1 px-4" onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button type="button" size="lg" className="flex-1 px-4" onClick={guardar} loading={enviando}>Guardar evaluación</Button>
        </div>
      )}>
      <div className="space-y-4">
        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Evaluación</legend>
          <Campo label="Fecha" requerido error={errores.fecha}>
            <DatePicker value={form.fecha} onChange={set('fecha')} max={hoy} aria-label="Fecha de la evaluación" className="w-full" />
          </Campo>
          <Campo label="Tipo" requerido error={errores.tipo}>
            <Select value={form.tipo} onChange={set('tipo')} aria-label="Tipo de evaluación" className="w-full">
              {TIPOS_EVALUACION.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          </Campo>
          <Campo label="Evaluado por" requerido error={errores.evaluador_nombre}>
            <Texto value={form.evaluador_nombre} onChange={set('evaluador_nombre')} />
          </Campo>
          <Campo label="Profesión">
            <Texto value={form.evaluador_profesion} onChange={set('evaluador_profesion')} placeholder="Ingeniero forestal, arborista…" />
          </Campo>
          <Campo label="Número de colegiado">
            <Texto value={form.evaluador_colegiado} onChange={set('evaluador_colegiado')} />
          </Campo>
          <Campo label="Inclinación (grados)" error={errores.inclinacion_grados}>
            <Texto value={form.inclinacion_grados} onChange={set('inclinacion_grados')} inputMode="decimal" />
          </Campo>
        </fieldset>

        <fieldset className="grid gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Estado del árbol</legend>
          <Campo label="Raíces"><Texto area value={form.raices} onChange={set('raices')} /></Campo>
          <Campo label="Tronco"><Texto area value={form.tronco} onChange={set('tronco')} /></Campo>
          <Campo label="Copa"><Texto area value={form.copa} onChange={set('copa')} /></Campo>
          <Campo label="Plagas o enfermedades"><Texto area value={form.plagas_enfermedades} onChange={set('plagas_enfermedades')} /></Campo>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-foreground">
            Nivel de riesgo<span className="text-error" aria-hidden="true"> *</span>
          </legend>
          <div role="radiogroup" aria-label="Nivel de riesgo" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {RIESGOS.map((r) => {
              const activo = form.riesgo === r;
              return (
                <button key={r} type="button" role="radio" aria-checked={activo} onClick={() => set('riesgo')(r)}
                  className={`h-11 rounded-lg text-sm font-semibold transition ${
                    activo ? `${RIESGO_PILL[r]} ring-2 ring-offset-1 ring-foreground/20` : 'border border-input bg-white text-foreground hover:bg-muted'
                  }`}>
                  {RIESGO_LABEL[r]}
                </button>
              );
            })}
          </div>
          {errores.riesgo && <p className="mt-1 text-xs text-error">{errores.riesgo}</p>}
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo label="Recomendaciones"><Texto area rows={3} value={form.recomendaciones} onChange={set('recomendaciones')} /></Campo>
          <Campo label="Acciones" ayuda="Poda, refuerzo, cambio de anclaje, retiro…">
            <Texto area rows={3} value={form.acciones} onChange={set('acciones')} />
          </Campo>
          <Campo label="Próxima evaluación" error={errores.proxima_evaluacion}>
            <DatePicker value={form.proxima_evaluacion} onChange={set('proxima_evaluacion')} min={form.fecha || undefined}
              clearable aria-label="Próxima evaluación" className="w-full" />
          </Campo>
          <div className="flex flex-col gap-1 text-sm font-medium text-foreground">
            <span>Informe del arborista (PDF)</span>
            {informe ? (
              <div className="flex h-10 items-center gap-2 rounded-lg border border-border bg-muted/40 px-2.5 text-sm">
                <FileText size={16} className="shrink-0 text-primary" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{informe.name}</span>
                <button type="button" onClick={() => setInforme(null)} aria-label="Quitar informe"
                  className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                  <X size={16} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" onClick={() => input.current?.click()} className="justify-start">
                <FileText size={16} className="mr-1.5" aria-hidden="true" />Adjuntar PDF
              </Button>
            )}
            <input ref={input} type="file" accept="application/pdf,.pdf" className="sr-only" aria-label="Informe PDF" onChange={elegirPdf} />
            {errores.informe && <span className="text-xs font-normal text-error">{errores.informe}</span>}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Después de guardada, la evaluación no se edita: si tiene un error, un administrador la anula con motivo y se registra otra.</p>
        {error && <p role="alert" className="rounded-lg bg-error/10 px-2.5 py-2 text-sm text-error">{error}</p>}
      </div>
    </Modal>
  );
}
